/**
 * aw-button - 3D Push Button Component
 *
 * A button with depth, shadow, and satisfying push animation.
 *
 * Attributes:
 *   variant: primary | secondary | danger | ghost (default: primary)
 *   size: sm | md | lg (default: md)
 *   disabled: boolean
 *   loading: boolean
 *   full: boolean (full width)
 *
 * Events:
 *   aw-click: Fired when button is clicked (not fired when disabled/loading)
 *
 * Slots:
 *   default: Button text content
 *   icon-left: Icon before text
 *   icon-right: Icon after text
 *
 * Usage:
 *   <aw-button variant="primary">Click me</aw-button>
 *   <aw-button variant="secondary" size="lg">Large Button</aw-button>
 *   <aw-button loading>Saving...</aw-button>
 */

import { AWElement } from './base';

export class AWButton extends AWElement {
  static observedAttributes = ['variant', 'size', 'disabled', 'loading', 'full'];

  // Variant color definitions using design tokens
  private static variantColors: Record<string, { bg: string; edge: string; shadow: string }> = {
    primary: {
      bg: 'var(--accent-primary)',
      edge: 'linear-gradient(to left, hsl(235deg 60% 35%) 0%, hsl(235deg 60% 45%) 8%, hsl(235deg 60% 45%) 92%, hsl(235deg 60% 35%) 100%)',
      shadow: 'hsl(235deg 60% 20% / 0.4)'
    },
    secondary: {
      bg: 'var(--bg-tertiary)',
      edge: 'linear-gradient(to left, hsl(0deg 0% 10%) 0%, hsl(0deg 0% 18%) 8%, hsl(0deg 0% 18%) 92%, hsl(0deg 0% 10%) 100%)',
      shadow: 'hsl(0deg 0% 0% / 0.3)'
    },
    danger: {
      bg: 'var(--status-error)',
      edge: 'linear-gradient(to left, hsl(350deg 80% 30%) 0%, hsl(350deg 80% 42%) 8%, hsl(350deg 80% 42%) 92%, hsl(350deg 80% 30%) 100%)',
      shadow: 'hsl(350deg 80% 20% / 0.4)'
    },
    ghost: {
      bg: 'transparent',
      edge: 'transparent',
      shadow: 'transparent'
    }
  };

  protected static get styles(): string {
    return `
      :host {
        display: inline-block;
        vertical-align: middle;
      }

      :host([full]) {
        display: block;
        width: 100%;
      }

      :host([disabled]) {
        pointer-events: none;
        opacity: 0.5;
      }

      .pushable {
        position: relative;
        border: none;
        background: transparent;
        padding: 0;
        cursor: pointer;
        outline-offset: 4px;
        transition: filter 250ms;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
        width: 100%;
      }

      .pushable:focus:not(:focus-visible) {
        outline: none;
      }

      .pushable:focus-visible {
        outline: 2px solid var(--accent-primary);
        outline-offset: 4px;
        border-radius: var(--radius-md);
      }

      /* Shadow layer - blur beneath button */
      .shadow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: var(--radius-md);
        background: var(--shadow-color, hsl(0deg 0% 0% / 0.25));
        filter: blur(4px);
        will-change: transform;
        transform: translateY(2px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
      }

      /* Edge layer - creates depth illusion */
      .edge {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: var(--radius-md);
        background: var(--edge-bg);
      }

      /* Front face - the visible button surface */
      .front {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        position: relative;
        border-radius: var(--radius-md);
        color: var(--text-color, white);
        background: var(--front-bg);
        will-change: transform;
        transform: translateY(-4px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
        font-family: var(--font-family);
        font-weight: 500;
        white-space: nowrap;
      }

      /* Ghost variant adjustments */
      :host([variant="ghost"]) .front {
        background: transparent;
        color: var(--text-primary);
        border: 1px solid var(--border-primary);
        transform: translateY(0);
      }

      :host([variant="ghost"]) .shadow,
      :host([variant="ghost"]) .edge {
        display: none;
      }

      :host([variant="ghost"]) .pushable:hover .front {
        background: var(--bg-tertiary);
        border-color: var(--border-secondary);
        transform: translateY(0);
      }

      :host([variant="secondary"]) .front {
        color: var(--text-primary);
        border: 1px solid var(--border-primary);
      }

      /* Size variants */
      :host([size="sm"]) .front {
        height: 32px;
        padding: 0 var(--space-3);
        font-size: var(--font-size-sm);
      }

      :host(:not([size])) .front,
      :host([size="md"]) .front {
        height: var(--height-button);
        padding: 0 var(--space-4);
        font-size: var(--font-size-base);
      }

      :host([size="lg"]) .front {
        height: 48px;
        padding: 0 var(--space-6);
        font-size: var(--font-size-md);
      }

      /* Hover state - lift up */
      .pushable:hover {
        filter: brightness(110%);
      }

      .pushable:hover .front {
        transform: translateY(-6px);
        transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      .pushable:hover .shadow {
        transform: translateY(4px);
        transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      /* Active state - push down */
      .pushable:active .front {
        transform: translateY(-2px);
        transition: transform 34ms;
      }

      .pushable:active .shadow {
        transform: translateY(1px);
        transition: transform 34ms;
      }

      /* Content wrapper - needs flex for icon alignment */
      .content {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      /* Loading state */
      :host([loading]) .pushable {
        pointer-events: none;
      }

      :host([loading]) .content {
        visibility: hidden;
      }

      .spinner {
        display: none;
        position: absolute;
        width: 18px;
        height: 18px;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        /* Natural spin: ease-in-out for looping, slightly slower for organic feel */
        animation: spin 900ms ease-in-out infinite;
      }

      :host([loading]) .spinner {
        display: block;
      }

      /* Natural rotation with variable speed - faster through middle, slower at edges */
      @keyframes spin {
        0% { transform: rotate(0deg); }
        50% { transform: rotate(200deg); }
        100% { transform: rotate(360deg); }
      }

      /* Reduced motion */
      @media (prefers-reduced-motion: reduce) {
        .shadow,
        .front,
        .pushable {
          transition: none;
        }

        .spinner {
          animation: none;
          border-color: currentColor;
          border-right-color: transparent;
        }
      }

      /* Slotted icon styling */
      ::slotted([slot="icon-left"]),
      ::slotted([slot="icon-right"]) {
        display: block;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
      }
    `;
  }

  protected render(): string {
    const variant = this.getAttribute('variant') || 'primary';
    const colors = AWButton.variantColors[variant] || AWButton.variantColors.primary;

    return `
      <button
        class="pushable"
        style="--front-bg: ${colors.bg}; --edge-bg: ${colors.edge}; --shadow-color: ${colors.shadow};"
        ${this.hasAttribute('disabled') ? 'disabled' : ''}
        part="button"
      >
        <span class="shadow" part="shadow"></span>
        <span class="edge" part="edge"></span>
        <span class="front" part="front">
          <span class="spinner" part="spinner"></span>
          <span class="content" part="content">
            <slot name="icon-left"></slot>
            <slot></slot>
            <slot name="icon-right"></slot>
          </span>
        </span>
      </button>
    `;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    const button = this.shadow.querySelector('button');
    if (button) {
      button.addEventListener('click', (e) => {
        if (this.hasAttribute('disabled') || this.hasAttribute('loading')) {
          e.preventDefault();
          return;
        }
        this.emit('aw-click', { originalEvent: e });
      });
    }
  }
}

// Export for extension
export default AWButton;
